import { useState } from "react";
import { Pencil, Trash2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { http, errMsg, formatPrice, prettyTitle, coverOf } from "@/lib/api";
import { ListingFormDialog } from "./ListingFormDialog";

export const ListingsAdmin = ({ items, reload }) => {
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(undefined);
  const [toDelete, setToDelete] = useState(null);

  const filtered = items.filter((l) => `${l.titolo} ${l.riferimento} ${l.comune}`.toLowerCase().includes(q.toLowerCase()));

  const save = async (payload) => {
    try {
      if (editing?.id) await http.put(`/admin/listings/${editing.id}`, payload);
      else await http.post("/admin/listings", payload);
      toast.success("Annuncio salvato");
      setEditing(undefined);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (l, key) => {
    try {
      const { id, created_at, updated_at, ...rest } = l;
      await http.put(`/admin/listings/${id}`, { ...rest, [key]: !l[key] });
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    try { await http.delete(`/admin/listings/${toDelete.id}`); toast.success("Annuncio eliminato"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  return (
    <div data-testid="admin-listings">
      <div className="flex flex-col sm:flex-row gap-3 justify-between mb-6">
        <div className="relative sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--mute-l)]" />
          <Input data-testid="admin-listings-search" placeholder="Cerca per titolo, rif., comune" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" />
        </div>
        <Button data-testid="admin-new-listing-button" onClick={() => setEditing(null)} className="gap-2"><Plus className="w-4 h-4" /> Nuovo annuncio</Button>
      </div>
      <div className="bg-white border overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-[var(--cream)] text-left">
            <tr className="eyebrow text-[0.65rem] text-[var(--mute-l)]">
              <th className="p-3">Immobile</th><th className="p-3">Comune</th><th className="p-3">Contratto</th><th className="p-3">Prezzo</th><th className="p-3">Evidenza</th><th className="p-3">Pubblicato</th><th className="p-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="border-t hover:bg-[var(--paper)] transition-colors" data-testid={`admin-listing-row-${l.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <img src={coverOf(l)} alt="" className="w-14 h-10 object-cover shrink-0" />
                    <div className="min-w-0"><p className="font-medium line-clamp-1 max-w-[280px]">{prettyTitle(l.titolo)}</p><p className="text-xs text-[var(--mute-l)]">{l.riferimento}</p></div>
                  </div>
                </td>
                <td className="p-3">{l.comune}</td>
                <td className="p-3">{l.contratto}</td>
                <td className="p-3 whitespace-nowrap">{formatPrice(l)}</td>
                <td className="p-3"><Switch data-testid={`admin-toggle-evidenza-${l.id}`} checked={l.in_evidenza} onCheckedChange={() => toggle(l, "in_evidenza")} /></td>
                <td className="p-3"><Switch data-testid={`admin-toggle-pubblicato-${l.id}`} checked={l.pubblicato} onCheckedChange={() => toggle(l, "pubblicato")} /></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" data-testid={`admin-edit-listing-${l.id}`} onClick={() => setEditing(l)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`admin-delete-listing-${l.id}`} onClick={() => setToDelete(l)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={7} className="p-10 text-center text-[var(--mute-l)]">Nessun annuncio</td></tr>}
          </tbody>
        </table>
      </div>
      {editing !== undefined && (
        <ListingFormDialog key={editing?.id || "new"} open onOpenChange={(o) => !o && setEditing(undefined)} listing={editing} onSave={save} />
      )}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'annuncio?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.riferimento} — l'operazione non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-delete-confirm" onClick={remove} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
